import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { usePrincipal } from '@/core/auth/appToken';
import { navigiereTab, oeffneVorbereitetenTab } from '@/shared/lib/vorbereiteterTab';
import { ApiError } from '@/models/apiError';
import { abrechnungKeys } from '../api/abrechnung.keys';
import { useCreateDatextLinkout } from '../api/useCreateDatextLinkout';
import { useDatextLinkoutStatus } from '../api/useDatextLinkoutStatus';
import { DATEXT_CUSTOMER_LINKOUT_ROLES } from '../model/datextLinkoutRoles';

/**
 * State of the top-navigation DATEXT action. Visible only for eligible customer roles whose
 * organization has credentials on file (fail-closed: loading and error hide it). Activation opens
 * a blank tab synchronously in the click (popup-blocker safe), requests the one-time launch URL
 * and navigates the tab to it; on failure the tab is closed and a toast explains why.
 *
 * Non-eligible roles trigger no request to the status endpoint. The launch URL is only handed to
 * the tab and never logged or stored.
 */
export function useDatextNavAction(): Readonly<{
  visible: boolean;
  pending: boolean;
  onActivate: () => void;
}> {
  const { t } = useTranslation('app');
  const principal = usePrincipal();
  const isEligible = principal !== null && DATEXT_CUSTOMER_LINKOUT_ROLES.includes(principal.role);
  const status = useDatextLinkoutStatus({ enabled: isEligible });
  const linkout = useCreateDatextLinkout();
  const queryClient = useQueryClient();

  function onActivate(): void {
    const tab = oeffneVorbereitetenTab();
    linkout.mutate(undefined, {
      onSuccess: ({ launchUrl }) => navigiereTab(tab, launchUrl),
      onError: (error) => {
        tab?.close();
        const code = error instanceof ApiError ? error.body.code : undefined;
        if (code === 'DATEXT_CREDENTIALS_MISSING') {
          void queryClient.invalidateQueries({ queryKey: abrechnungKeys.datextStatus() });
          toast.error(t('datextLinkout.error.credentialsMissing'));
        } else if (code === 'DATEXT_PUBLIC_KEY_UNAVAILABLE') {
          toast.error(t('datextLinkout.error.unavailable'));
        } else {
          toast.error(t('datextLinkout.error.generic'));
        }
      },
    });
  }

  return {
    visible: isEligible && status.data?.credentialsPresent === true,
    pending: linkout.isPending,
    onActivate,
  };
}
